import React, { useState, useEffect, useRef } from 'react';
// import { ApolloClient, InMemoryCache, ApolloProvider, gql } from '@apollo/client';
import { useQuery, gql, useSubscription, useMutation } from '@apollo/client';
import { getToken } from '../hooks/AuthProvider';
// import { useAuth } from '@clerk/clerk-react';

type props = {};

type Notification = {
  _id: string;
  message: string;
  read: boolean;
  type: NotificationType | null;
  createdAt: Date | string | number; // Can be Date object, ISO string, or Unix timestamp
  profilePic: string | null; // if is null then the notification is from the system ...
  silent: boolean; // if true then no sound will be played
}

type NotificationType = "info" | "success" | "warning" | "error"

const typeIcons: Record<NotificationType, React.ReactNode> = {
  info: <i className="" />,
  success: <i className="" />,
  warning: <i className="fa-solid fa-exclamation text-yellow-500" />,
  error: <i className="" />,
}

type state = {
};

const GET_NOTIFICATIONS = gql`
  query getNotifications($token: String!) {
    getNotifications(token: $token) {
      _id
      userID
      message
      senderID
      read
      createdAt
      profilePic
      username
      displayName
    }
  }
`;

const UPDATE_NOTIFICATION = gql`
  mutation markRead($token: String!, $notificationID: String!) {
    markRead(token: $token, notificationID: $notificationID)
  }
`;

const GETNOTIFICATIONPREFERENCES = gql`
  query getNotificationPreferences($token: String!) {
    getNotificationPreferences(token: $token) {
      email
      push
      posts
      messages
      livestreams
      soundAlert
    }
  }
`;

const UpdateNotifications = gql`
  mutation updateNotificationPreferences(
    $token: String!,
    $email: Boolean!,
    $push: Boolean!,
    $posts: Boolean!,
    $messages: Boolean!,
    $livestreams: Boolean!,
    $soundAlert: Boolean!
  ) {
    updateNotificationPreferences(
      token: $token,
      email: $email,
      push: $push,
      posts: $posts,
      messages: $messages,
      livestreams: $livestreams,
      soundAlert: $soundAlert
    ) {
      email
      push
      posts
      messages
      livestreams
      soundAlert
    }
  }
`;

function formatRelativeTime(dateInput: string | Date | number): string {
  try {
    let date: Date;
    
    // Handle number (Unix timestamp in milliseconds) or numeric string
    if (typeof dateInput === 'number') {
      date = new Date(dateInput);
    } else if (typeof dateInput === 'string' && /^\d+$/.test(dateInput)) {
      // It's a numeric string (Unix timestamp)
      date = new Date(parseInt(dateInput, 10));
    } else {
      // It's a date string or Date object
      date = new Date(dateInput);
    }
    
    console.log('formatRelativeTime - dateInput:', dateInput, 'type:', typeof dateInput, 'parsed date:', date, 'isValid:', !isNaN(date.getTime()));
    
    if (isNaN(date.getTime())) {
      console.log('Invalid date:', dateInput, 'trying fallback parsing');
      // Try parsing as number if it's a string
      if (typeof dateInput === 'string') {
        const num = Number(dateInput);
        if (!isNaN(num)) {
          date = new Date(num);
          console.log('Fallback parsed as number:', date, 'isValid:', !isNaN(date.getTime()));
        }
      }
      
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
    }

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    
    console.log('Time difference:', diffInMs, 'ms');
    
    // Handle future dates (shouldn't happen, but just in case)
    if (diffInMs < 0) {
      const result = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      console.log('Future date, returning:', result);
      return result || date.toLocaleDateString();
    }

    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInWeeks = Math.floor(diffInDays / 7);

    let result = '';
    if (diffInSeconds < 60) {
      result = 'just now';
    } else if (diffInMinutes < 60) {
      result = `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      result = `${diffInHours}${diffInHours === 1 ? ' hr' : ' hrs'} ago`;
    } else if (diffInDays < 7) {
      result = `${diffInDays}${diffInDays === 1 ? ' day' : ' days'} ago`;
    } else {
      // Show actual date for notifications older than 1 week
      const dateStr = date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
      });
      result = dateStr || date.toLocaleDateString();
    }
    
    console.log('formatRelativeTime result:', result, 'for input:', dateInput);
    return result || 'Unknown time';
  } catch (e) {
    console.error('Error formatting relative time:', e, dateInput);
    return 'Error';
  }
}

function NotificationsList() {
  const [isOpen, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const [pushNotifcations, setPushNotifications] = useState(false);
  const [soundAlerts, setSoundAlerts] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [posts, setPosts] = useState(false);
  const [livestreams, setLivestreams] = useState(false);
  const [messages, setMessages] = useState(false);
  // const [notifications, setNotifications] = useState(Notification[])([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  // const [markedAsRead, setMarkedAsRead] = useState<string | null>(null);
  // const { getToken } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [updateNotification] = useMutation(UPDATE_NOTIFICATION);
  const [updateSettings] = useMutation(UpdateNotifications);

  useEffect(() => {
    setToken(getToken());
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && token) {
        NotificationQuery.refetch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [token]);

  const query = useQuery(GET_NOTIFICATIONS, {
    skip: !token,
    variables: { token },
  });

  const NotificationQuery = useQuery(GETNOTIFICATIONPREFERENCES, {
    skip: !token,
    variables: { token },
  });

  // function getInitNotifications() {
  //   return getToken().then(token => {
  //       const getInitQuery = gql`
  //       query getNotifications($token: String!) {
  //         getNotifications(token: $token) {
  //           _id
  //           userID
  //           message
  //           senderID
  //           read
  //           createdAt
  //         }
  //       }
  //       `;

  //       return { query: getInitQuery, variables: { token } };
  //   });
  //   }

  // // console.log(getInitNotifications().then((res: any) => {return res}));
  // getInitNotifications().then((res: any) => {
  //   console.log(res);
  // })



  // const query = useQuery(getInitQuery);

  useEffect(() => {
    if (!query.loading && query.data) {
      // console.log(query);
      if (query.data.getNotifications === null) {
        setLoading(false);
      } else {
        console.log(query.data.getNotifications);
        // Sort notifications by createdAt in descending order (most recent first)
        const sortedNotifications = [...query.data.getNotifications].sort((a, b) => {
          const dateA = typeof a.createdAt === 'number' ? a.createdAt : new Date(a.createdAt).getTime();
          const dateB = typeof b.createdAt === 'number' ? b.createdAt : new Date(b.createdAt).getTime();
          return dateB - dateA; // Descending order (newest first)
        });
        setNotifications(sortedNotifications);
        setLoading(false);

        for (let i = 0; i < sortedNotifications.length; i++) {
          if (!sortedNotifications[i].read) {
            setUnread((prevUnread) => prevUnread + 1);
          }
        }
      }
    }
  }, [query.loading, query.data]); // Only update state when query data changes

  useEffect(() => {
    if (!NotificationQuery.loading && NotificationQuery.data) {
      const prefs = NotificationQuery.data.getNotificationPreferences[0];
      setPushNotifications(prefs.push);
      setSoundAlerts(prefs.soundAlert);
      setEmailNotifications(prefs.email);
      setPosts(prefs.posts);
      setLivestreams(prefs.livestreams);
      setMessages(prefs.messages);
      localStorage.setItem('notificationSettings', JSON.stringify(prefs));
    }
  }, [NotificationQuery.loading, NotificationQuery.data]);

  // if (query.loading === false) {
  //   setNotifications(query.data.getNotifications);
  //   setLoading(false);
  // }
  // console.log(query);

  const getNotifications = gql`
  subscription {
    notificationSent {
      _id
      userID
      message
      senderID
      read
      createdAt
      profilePic
      username
      displayName
    }
  }`

  const { error, data } = useSubscription<{ notificationSent: Notification }>(getNotifications, {
    onData: ( subscriptionData ) => {
      // console.log('update');
      console.log(subscriptionData);

      if (subscriptionData?.data?.data?.notificationSent && !subscriptionData.data.loading) {
        setNotifications((prevNotifications) => [
          // @ts-ignore
          subscriptionData.data.data.notificationSent,
          ...prevNotifications,
        ]);
        setUnread((prevUnread) => prevUnread + 1); // Optionally update unread count

        if (Notification.permission === "granted" && pushNotifcations === true) {
          // new Notification(subscriptionData.data.data.notificationSent.message);
          var notification = new Notification('Notification title', {
            // body: 'Hey there! You\'ve been notified!',
            body: subscriptionData.data.data.notificationSent.message,
           });
           notification.onclick = function() {
            window.open('https://guardedcontent.com/app/');
          };
        } else if (Notification.permission !== "denied") {
          if (soundAlerts) {
            let sound: HTMLAudioElement = new Audio('/ping.mp3');
            sound.load();
            sound.play();
          }
        }
      }
    },
    onError: (error) => {
      console.error(`Subscription error:`, error);
    }
  });

  // console.log('SUBERR: ')
  // console.log(error);

  // setNotifications([...notifications, data.notificationSent]);

  // console.log(data);

  function setIsOpen(value: boolean) {
    setOpen(value);
  }

  const handleSettingsClick = () => {
    setIsSettingsOpen(!isSettingsOpen);
  };

  async function dismissNotification(id: string, index: number) {
    // Remove notification from list
    const updatedNotifications = [...notifications];
    updatedNotifications.splice(index, 1);
    setNotifications(updatedNotifications);
    const oldUnread = unread;
    setUnread(oldUnread - 1);

    var newToken = getToken();

    updateNotification({
      variables: { token: newToken, notificationID: id },
    })
  }

  function changeSettings(value: boolean, setting: string) {
    switch(setting) {
      case 'notifications':
        // setPushNotifications(value);
        if (value === true) {
          if (Notification.permission === 'default') {
            Notification.requestPermission().then((result) => {
              console.log(result);
              if (result === 'granted') {
                setPushNotifications(value);
              } else {
                setPushNotifications(false);
              }
            })
          } else if (Notification.permission === 'denied') {
            setPushNotifications(false);
            // Prompt user that an error has occurred.
            alert('You have denied notifications. Please enable them in your browser settings.');
          } else {
            setPushNotifications(value);
          }
        } else {
          setPushNotifications(value);
        }
        break;
      case 'alerts':
        setSoundAlerts(value);
        break;
      case 'posts':
        setPosts(value);
        break;
      case 'livestreams':
        setLivestreams(value);
        break;
      case 'messages':
        setMessages(value);
        break;
    }
    // if (value === true) {
    //   Notification.requestPermission().then((result) => {
    //     console.log(result);
    //   })
    // }
  }

  async function saveSettings() {
    const { data } = await updateSettings({
      variables: { token, email: emailNotifications, push: pushNotifcations, posts, messages, livestreams, soundAlert: soundAlerts },
    });

    if (data?.updateNotificationPreferences) {
      localStorage.setItem('notificationSettings', JSON.stringify(data.updateNotificationPreferences));
    }

    setIsSettingsOpen(false);
  }

  // useEffect(() => {
  //   if (markedAsRead && token) {
  //     useQuery(updateNotification, {
  //       variables: { token, notificationID: markedAsRead },
  //     });
  //   }
  // }, [markedAsRead, token]);

  // if (loading) return <p>Loading ...</p>;
  // if (error) return <p>Error : {error.message}</p>;

  return <div className="relative" ref={dropdownRef}>
  <button
    onClick={() => setIsOpen(!isOpen)}
    className="relative py-2 -mr-2 sm:-mr-2 md:mr-1 lg:mr-1 xl:mr-1 2xl:mr-1 rounded-full text-gray-600 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
    aria-label="Toggle notifications"
  >
    <i className="fa-regular fa-bell mx-2 text-xl" />
    {unread > 0 && (
      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
        {unread}
      </span>
    )}
  </button>
  {isOpen && (
    <div className="absolute right-0 mt-2 w-96 bg-gray-900 rounded-xl shadow-2xl border border-gray-700 z-10 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-700/50 bg-gray-800/50 backdrop-blur-sm">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            {!isSettingsOpen && unread > 0 && (
              <span className="h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
            )}
            <h2 className="text-lg font-semibold text-white">
              {isSettingsOpen ? 'Notification Settings' : 'Notifications'}
            </h2>
            {!isSettingsOpen && unread > 0 && (
              <span className="px-2 py-0.5 bg-red-600/20 text-red-400 text-xs font-medium rounded-full">
                {unread} new
              </span>
            )}
          </div>
          <button
            onClick={handleSettingsClick}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-all duration-200"
            aria-label={isSettingsOpen ? 'Back to notifications' : 'Notification settings'}
          >
            <i className={`fas ${isSettingsOpen ? 'fa-arrow-left' : 'fa-cog'} text-sm`}></i>
          </button>
        </div>
      </div>

      {/* Content */}
      {(() => {
        if (isSettingsOpen) {
          return (
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* General Settings */}
              <div className="space-y-2.5">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
                  General Settings
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center justify-between py-2 px-2.5 rounded-lg hover:bg-gray-800/50 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 bg-blue-500/10 rounded-lg group-hover:bg-blue-500/20 transition-colors w-8 h-8 flex items-center justify-center">
                        <i className="fas fa-bell text-blue-400 text-xs"></i>
                      </div>
                      <div>
                        <span className="text-white font-medium text-sm block">Push Notifications</span>
                        <span className="text-gray-400 text-xs">Browser notifications</span>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      id="notifications"
                      className="h-4 w-4 accent-red-600 rounded cursor-pointer"
                      checked={pushNotifcations}
                      onChange={(e) => changeSettings(e.target.checked, 'notifications')}
                    />
                  </label>
                  <label className="flex items-center justify-between py-2 px-2.5 rounded-lg hover:bg-gray-800/50 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 bg-purple-500/10 rounded-lg group-hover:bg-purple-500/20 transition-colors w-8 h-8 flex items-center justify-center">
                        <i className="fas fa-volume-up text-purple-400 text-xs"></i>
                      </div>
                      <div>
                        <span className="text-white font-medium text-sm block">Sound Alerts</span>
                        <span className="text-gray-400 text-xs">Play sound for new notifications</span>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      id="alerts"
                      className="h-4 w-4 accent-red-600 rounded cursor-pointer"
                      checked={soundAlerts}
                      onChange={(e) => changeSettings(e.target.checked, 'alerts')}
                    />
                  </label>
                </div>
              </div>

              {/* Notification Preferences */}
              <div className="space-y-2.5 pt-1.5 border-t border-gray-700/50">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1">
                  Notification Types
                </h3>
                <div className="space-y-2">
                  <label className="flex items-center justify-between py-2 px-2.5 rounded-lg hover:bg-gray-800/50 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 bg-green-500/10 rounded-lg group-hover:bg-green-500/20 transition-colors w-8 h-8 flex items-center justify-center">
                        <i className="fas fa-image text-green-400 text-xs"></i>
                      </div>
                      <span className="text-white font-medium text-sm">Posts</span>
                    </div>
                    <input
                      type="checkbox"
                      id="posts"
                      className="h-4 w-4 accent-red-600 rounded cursor-pointer"
                      checked={posts}
                      onChange={(e) => changeSettings(e.target.checked, 'posts')}
                    />
                  </label>
                  <label className="flex items-center justify-between py-2 px-2.5 rounded-lg hover:bg-gray-800/50 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 bg-red-500/10 rounded-lg group-hover:bg-red-500/20 transition-colors w-8 h-8 flex items-center justify-center">
                        <i className="fas fa-video text-red-400 text-xs"></i>
                      </div>
                      <span className="text-white font-medium text-sm">Livestreams</span>
                    </div>
                    <input
                      type="checkbox"
                      id="livestreams"
                      className="h-4 w-4 accent-red-600 rounded cursor-pointer"
                      checked={livestreams}
                      onChange={(e) => changeSettings(e.target.checked, 'livestreams')}
                    />
                  </label>
                  {/* <label className="flex items-center justify-between py-2 px-2.5 rounded-lg hover:bg-gray-800/50 transition-colors cursor-pointer group">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 bg-yellow-500/10 rounded-lg group-hover:bg-yellow-500/20 transition-colors w-8 h-8 flex items-center justify-center">
                        <i className="fas fa-envelope text-yellow-400 text-xs"></i>
                      </div>
                      <span className="text-white font-medium text-sm">Direct Messages</span>
                    </div>
                    <input
                      type="checkbox"
                      id="messages"
                      className="h-4 w-4 accent-red-600 rounded cursor-pointer"
                      defaultChecked={messages}
                      onChange={(e) => changeSettings(e.target.checked, 'messages')}
                    />
                  </label> */}
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-2.5">
                <button
                  onClick={() => saveSettings()}
                  className="w-full bg-red-600 hover:bg-red-500 text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 shadow-lg shadow-red-600/20 hover:shadow-red-600/30 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900 text-sm"
                >
                  Save Settings
                </button>
              </div>
            </div>
          );
        } else {
          if (loading) {
            return (
              <div className="p-8 flex flex-col items-center justify-center min-h-[200px]">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600 mb-4"></div>
                <p className="text-gray-400 text-sm">Loading notifications...</p>
              </div>
            );
          } else if (error) {
            return (
              <div className="p-8 flex flex-col items-center justify-center min-h-[200px]">
                <div className="p-3 bg-red-500/10 rounded-full mb-4">
                  <i className="fas fa-exclamation-triangle text-red-400 text-xl"></i>
                </div>
                <p className="text-red-400 font-medium mb-1">Error loading notifications</p>
                <p className="text-gray-500 text-sm text-center">{error.message}</p>
              </div>
            );
          } else if (notifications.length === 0) {
            return (
              <div className="p-8 flex flex-col items-center justify-center min-h-[200px]">
                <div className="p-4 bg-gray-800 rounded-full mb-4">
                  <i className="fas fa-bell-slash text-gray-500 text-2xl"></i>
                </div>
                <p className="text-white font-medium mb-1">No notifications</p>
                <p className="text-gray-500 text-sm text-center">You're all caught up!</p>
              </div>
            );
          } else {
            return (
              <div className="max-h-[60vh] overflow-y-auto">
                <ul className="divide-y divide-gray-700/50">
                  {notifications.slice(0, 5).map((notification, index) => (
                    <li
                      key={notification._id}
                      className="group relative p-4 hover:bg-gray-800/50 transition-all duration-200"
                    >
                      <div className="flex items-start gap-3">
                        {/* Profile Picture / Icon */}
                        <div className="flex-shrink-0 mt-0.5">
                          {notification.profilePic ? (
                            <img
                              src={notification.profilePic}
                              alt="Profile"
                              className="w-10 h-10 rounded-full ring-2 ring-gray-700 group-hover:ring-gray-600 transition-all"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center ring-2 ring-gray-700 group-hover:ring-gray-600 transition-all">
                              <i className="fas fa-triangle-exclamation text-yellow-500 text-sm"></i>
                            </div>
                          )}
                        </div>
                        
                        {/* Notification Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm leading-relaxed break-words">
                            {notification.message}
                          </p>
                          {notification.createdAt ? (
                            <p className="text-gray-500 text-xs mt-1">
                              {formatRelativeTime(notification.createdAt) || 'No date'}
                            </p>
                          ) : (
                            <p className="text-gray-500 text-xs mt-1">No createdAt</p>
                          )}
                        </div>

                        {/* Dismiss Button */}
                        <button
                          className="flex-shrink-0 p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 focus:outline-none focus:opacity-100"
                          onClick={() => dismissNotification(notification._id, index)}
                          aria-label="Dismiss notification"
                        >
                          <i className="fas fa-times text-xs"></i>
                        </button>
                      </div>
                      
                      {/* Unread Indicator */}
                      {!notification.read && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-red-500 rounded-r-full"></div>
                      )}
                    </li>
                  ))}
                </ul>
                
                {notifications.length > 5 && (
                  <div className="p-4 border-t border-gray-700/50 bg-gray-800/30">
                    <p className="text-center text-gray-400 text-xs">
                      Showing 5 of {notifications.length} notifications
                    </p>
                  </div>
                )}
              </div>
            );
          }
        }
      })()}
    </div>
  )}

  {/* Settings Modal */}
  {/* {isSettingsOpen && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-20">
      <div className="bg-gray-800 p-6 rounded-md w-80">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-white">Notification Settings</h3>
          <button
            onClick={handleSettingsClick}
            className="text-white hover:text-gray-500"
            aria-label="Close settings"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="space-y-4 text-white">
          <div className="flex items-center space-x-2">
            <input type="checkbox" id="enableNotifications" className="h-4 w-4" />
            <label htmlFor="enableNotifications">Enable Notifications</label>
          </div>
          <div className="flex items-center space-x-2">
            <input type="checkbox" id="soundAlerts" className="h-4 w-4" />
            <label htmlFor="soundAlerts">Sound Alerts</label>
          </div>
          <div className="flex items-center space-x-2">
            <input type="checkbox" id="emailNotifications" className="h-4 w-4" />
            <label htmlFor="emailNotifications">Email Notifications</label>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSettingsClick}
            className="bg-indigo-500 text-white p-2 rounded-md hover:bg-indigo-600 transition"
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  )} */}
</div>
}

export default class Notifications extends React.PureComponent<props, state> {
    constructor(props: any) {
        super(props);
        this.state = {
        }
    }

    render() {
        return <div className="mx-2">
            <NotificationsList />
        </div>
    }
}